import React from 'react';
import { AxiosError } from 'axios';
import { makeStyles, Snackbar, Icon, Typography } from '@material-ui/core';
import MuiAlert, { AlertProps, Color } from '@material-ui/lab/Alert';

import { ErrorHelper } from '../ErrorHelper';
import { IErrorResponse } from '../ErrorResponse';

export interface IAlertDialogProps {
    open: boolean,
    severity: Color,
    message: string | JSX.Element
    onClose?: () => void,
}

export const alertDialogPropsInitial: IAlertDialogProps =
{
    open: false,
    severity: 'success',
    message: ''
};

function Alert(props: JSX.IntrinsicAttributes & AlertProps) {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const useStyles = makeStyles(() => ({
    icon: {
        fontSize: '46px'
    },
    alert: {
        maxWidth: '350px'
    }
}));

export function createAlertProps() {
    const [alertProps, setAlertProps] = React.useState<IAlertDialogProps>(alertDialogPropsInitial);

    React.useEffect(() => {
        if (!alertProps.onClose) {
            setAlertProps({ 
                ...alertProps,
                onClose: () => {
                    setAlertProps({
                        message: alertProps.message,
                        severity: alertProps.severity,
                        open: false,
                        onClose: () => { ''; } 
                    });
                }
            });
        }
    }, [alertProps]);

    const showError = (message: string, error?: AxiosError) => {
        let errorResponse: IErrorResponse = {
            message: '',
            status: null,
            errorList: []
        };

        if (error)
            errorResponse = ErrorHelper(error);

        setAlertProps({
            message: message + errorResponse.message,
            severity: 'error',
            open: true,
        });
    };

    const showMessageBySeverity = (severity: Color) => (message: string) => {
        setAlertProps({
            message: message,
            severity: severity,
            open: true,
        });
    };

    return {
        alertProps: alertProps,
        setAlertProps: setAlertProps,
        showError: showError,
        showWarning: showMessageBySeverity('warning'),
        showSuccess: showMessageBySeverity('success'),
    };
}

export function AlertDialog({ open, onClose, severity, message }: IAlertDialogProps) {

    const classes = useStyles();

    function getIcon(): string {
        switch (severity) {
        case 'error': return 'close';
        case 'success': return 'done';
        case 'warning': return 'warning';
        default:
            return '';
        }
    }

    function getMessage() {
        if (typeof message == 'string')
            return message.split('<br/>').map((line) => { if (line) return <> {line} <br /> </>; });
        else
            return message;
    }

    return (
        <Snackbar
            open={open}
            autoHideDuration={6000}
            onClose={onClose} >
            <Alert
                className={classes.alert}
                variant="standard"
                onClose={onClose}
                severity={severity}
                icon={<Icon className={`material-icons-round ${classes.icon}`}>{ getIcon() }</Icon>}>
                <Typography variant="caption">
                    {getMessage()}
                </Typography>
            </Alert>
        </Snackbar>
    );
}